"use client";

import useResize from "@/app/hooks/useResize";
import { Box, Typography } from "@mui/material";
import { ChevronDown, X } from "lucide-react";
import { CSSProperties, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";

const LINE_CLAMP = 3;
const LINE_HEIGHT = 1.9;

// How far past the button the words go on fading, so the last one to survive the cut
// ends rather than stops.
const FADE_RUN_IN = 28;

// How tall the open card is allowed to grow before it scrolls inside itself. Nothing
// written for this slot from now on comes close; the articles left over from when this
// text lived in the buying guide do.
const CARD_MAX_HEIGHT = 340;

// The entities quill writes by name. Anything numeric is read off its code point.
const NAMED_ENTITIES: Record<string, string> = {
    nbsp: " ",
    zwnj: "‌",
    amp: "&",
    lt: "<",
    gt: ">",
    quot: '"',
    apos: "'",
    laquo: "«",
    raquo: "»",
    hellip: "…",
    mdash: "—",
    ndash: "–",
};

// Where one block ends and the next begins, so the last word of a paragraph does not
// run into the first word of the one after it.
const BLOCK_BOUNDARY = /<\/(?:p|div|li|ul|ol|h[1-6]|tr|td|th|blockquote|section)\s*>|<br\s*\/?>/gi;

/**
 * The words of an html fragment, with the markup dropped. The slot holds one short
 * paragraph, but the pages written before this text moved down into the buying guide
 * still carry full articles - headings, tables, images - and none of that survives
 * being clamped to three lines.
 */
export function htmlToText(html: string): string {
    return html
        .replace(/<(script|style)[\s\S]*?<\/\1>/gi, " ")
        .replace(BLOCK_BOUNDARY, " ")
        .replace(/<[^>]*>/g, "")
        .replace(/&#(\d+);/g, (_match, code: string) => String.fromCodePoint(Number(code)))
        .replace(/&#x([0-9a-f]+);/gi, (_match, code: string) => String.fromCodePoint(Number.parseInt(code, 16)))
        .replace(/&([a-z]+);/gi, (match, name: string) => NAMED_ENTITIES[name.toLowerCase()] ?? match)
        .replace(/\s+/g, " ")
        .trim();
}

/**
 * The blurb under the heading and the price. It is capped at three lines, and anything
 * longer opens in a card laid over the page rather than by pushing the list down: the
 * header has to measure the same whether the reader opened it or not.
 */
export default function PlpHeroIntro({ html, tone = "light" }: { html: string; tone?: "light" | "dark" }) {
    const text = useMemo(() => htmlToText(html), [html]);
    const clampRef = useRef<HTMLParagraphElement>(null);
    const buttonRef = useRef<HTMLButtonElement>(null);
    const cardRef = useRef<HTMLDivElement>(null);
    const [open, setOpen] = useState(false);
    const [isClamped, setIsClamped] = useState(false);
    const [buttonWidth, setButtonWidth] = useState(0);
    const windowWidth = useResize();
    const ink = INK[tone];

    // Whether three lines actually hid anything is a question about the text as it was
    // laid out, so it is asked of the element rather than of the string.
    useLayoutEffect(() => {
        const element = clampRef.current;
        if (!element) return;

        setIsClamped(element.scrollHeight - element.clientHeight > 1);
    }, [text, windowWidth]);

    // How much of the last line the button stands on. Measured rather than guessed:
    // the label is a fixed string but the font it lands in is not.
    useLayoutEffect(() => {
        if (!isClamped) return setButtonWidth(0);
        setButtonWidth(buttonRef.current?.offsetWidth ?? 0);
    }, [isClamped, text, windowWidth]);

    const close = useCallback(() => setOpen(false), []);

    useEffect(() => {
        if (!open) return;

        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Escape") close();
        };
        // Pointer down rather than click: the button that opened the card would
        // otherwise close it again on the very same event.
        const onPointerDown = (event: PointerEvent) => {
            if (cardRef.current?.contains(event.target as Node)) return;
            close();
        };

        document.addEventListener("keydown", onKeyDown);
        document.addEventListener("pointerdown", onPointerDown);

        return () => {
            document.removeEventListener("keydown", onKeyDown);
            document.removeEventListener("pointerdown", onPointerDown);
        };
    }, [open, close]);

    if (!text) return null;

    return (
        // Sits between the heading it belongs to and the price line under it, and
        // the line heights around it leave no room of their own, so it carries its
        // own. Without the lower half the price reads as part of the blurb.
        <Box sx={{ position: "relative", maxWidth: 640, mt: 0.75, mb: 1.25 }}>
            {/* Left in place while the card is open: it is what holds the header's
                height, so hiding it from the reader is as far as this can go. */}
            <Box sx={{ visibility: open ? "hidden" : "visible" }}>
                <Typography
                    ref={clampRef}
                    style={buttonWidth ? fadeLastLine(buttonWidth) : undefined}
                    sx={{
                        display: "-webkit-box",
                        WebkitBoxOrient: "vertical",
                        WebkitLineClamp: LINE_CLAMP,
                        overflow: "hidden",
                        color: ink.body,
                        fontSize: { xs: 12.5, sm2: 13 },
                        lineHeight: LINE_HEIGHT,
                        textAlign: "justify",
                    }}
                >
                    {text}
                </Typography>

                {/* Whether three lines hid anything can only be answered once the text
                    has been laid out, so this button is never in the server's html and
                    arrives one frame later. Given a row of its own it would push the
                    price and the buttons below it down as it appeared, so it takes no
                    room at all: it stands on the tail of the third line, where the words
                    beneath it have been faded out rather than painted over - a patch of
                    flat colour only disappears on a flat background, and a header may be
                    sitting on a gradient. */}
                {isClamped && (
                    <Box
                        component="button"
                        ref={buttonRef}
                        type="button"
                        onClick={() => setOpen(true)}
                        aria-expanded={false}
                        sx={{
                            ...introButtonSx,
                            color: ink.strong,
                            position: "absolute",
                            // A line box tall and sitting on the floor of the last one,
                            // so the label keeps the baseline of the words it stands
                            // among rather than hanging below them.
                            bottom: 0,
                            height: `${LINE_HEIGHT}em`,
                            fontSize: { xs: 12.5, sm2: 13 },
                            // Written as the opposite of where it is wanted: the rtl
                            // transformer flips it, and an rtl line ends on the left.
                            right: 0,
                        }}
                    >
                        {/* First in the dom, so in rtl it lands on the right, against
                            the words. The clamp puts an ellipsis of its own at the end
                            of the line, but that end is underneath this button, so the
                            cut needs one the reader can see. */}
                        <Box component="span" aria-hidden sx={{ color: ink.body, fontWeight: 400 }}>…</Box>
                        مشاهده بیشتر
                        <ChevronDown size={14} strokeWidth={2.2} color={ink.icon} aria-hidden />
                    </Box>
                )}
            </Box>

            {open && (
                <Box
                    ref={cardRef}
                    sx={{
                        position: "absolute",
                        // Pulled out far enough to read as a card around the words
                        // rather than a box inside them, and no further: the page
                        // gutter is 16px, and the card has to stay inside it.
                        top: -10,
                        left: -8,
                        right: -8,
                        zIndex: 5,
                        p: 1.5,
                        bgcolor: ink.card,
                        border: "1px solid",
                        borderColor: ink.cardBorder,
                        borderRadius: 1,
                        boxShadow: "0 1px 2px rgba(16, 24, 40, 0.05), 0 1px 3px rgba(16, 24, 40, 0.04)",
                        maxHeight: CARD_MAX_HEIGHT,
                        overflowY: "auto",
                        overscrollBehavior: "contain",
                    }}
                >
                    <Typography sx={{ color: ink.cardBody, fontSize: { xs: 12.5, sm2: 13 }, lineHeight: LINE_HEIGHT, textAlign: "justify" }}>
                        {text}
                    </Typography>
                    <Box
                        component="button"
                        type="button"
                        onClick={close}
                        aria-expanded
                        sx={{ ...introButtonSx, color: ink.strong, mt: 1 }}
                    >
                        بستن
                        <X size={14} strokeWidth={2.2} color={ink.icon} aria-hidden />
                    </Box>
                </Box>
            )}
        </Box>
    );
}

/**
 * Fades the end of the last line out instead of hiding it behind the button. Written
 * onto `style` rather than `sx`: the rtl processor mirrors the direction and the stops
 * of a gradient, and a mask rewritten that way clears the wrong end of the line.
 *
 * Two layers - the lines above the last one stay fully opaque, and the last one fades
 * to nothing at the end it is read towards, which in rtl is its left.
 */
function fadeLastLine(buttonWidth: number): CSSProperties {
    // Nothing at all under the button, then the words fading back in over the run-in.
    // A gradient that only reaches full strength at the button's far edge leaves the
    // text showing through most of the label.
    const mask = `linear-gradient(to right, transparent 0, transparent ${buttonWidth}px, #000 ${buttonWidth + FADE_RUN_IN}px), linear-gradient(#000, #000)`;
    const size = `100% ${LINE_HEIGHT}em, 100% calc(100% - ${LINE_HEIGHT}em)`;
    const position = "left bottom, left top";

    return {
        WebkitMaskImage: mask,
        WebkitMaskSize: size,
        WebkitMaskPosition: position,
        WebkitMaskRepeat: "no-repeat, no-repeat",
        maskImage: mask,
        maskSize: size,
        maskPosition: position,
        maskRepeat: "no-repeat, no-repeat",
    };
}

// A header drawn on a dark band or a gradient needs the blurb and its card in the
// inverse; everywhere else reads the greys the brand pages have always used.
const INK = {
    light: {
        body: "grey.600",
        strong: "grey.900",
        // Lucide paints the stroke with whatever string it is handed, so this one is a
        // real colour rather than one of the theme's names.
        icon: "#212121",
        card: "#fff",
        cardBorder: "grey.200",
        cardBody: "grey.700",
    },
    dark: {
        body: "rgba(255,255,255,.66)",
        strong: "#fff",
        icon: "#fff",
        card: "#1c1e22",
        cardBorder: "rgba(255,255,255,.16)",
        cardBody: "rgba(255,255,255,.82)",
    },
} as const;

const introButtonSx = {
    display: "inline-flex",
    alignItems: "center",
    gap: 0.5,
    p: 0,
    border: 0,
    bgcolor: "transparent",
    font: "inherit",
    fontSize: 12.5,
    fontWeight: 700,
    cursor: "pointer",
    "&:hover": { textDecoration: "underline" },
} as const;
